import React, { useState, useRef, useEffect } from 'react';

// ⚠️ Remplace par l'URL de ta fonction Vercel une fois déployée
const API_URL = 'https://decode-skating.vercel.app/api/decode-fiche';

const MAX_DIMENSION = 1400; // redimensionne les grandes photos avant envoi
const ACCESS_STORAGE_KEY = 'fiche-decode-access-code';

// Redimensionne l'image côté navigateur (via canvas) pour réduire son poids
// avant de l'envoyer à l'API — plus rapide et moins cher.
const resizeImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          if (width > height) {
            height = Math.round((height * MAX_DIMENSION) / width);
            width = MAX_DIMENSION;
          } else {
            width = Math.round((width * MAX_DIMENSION) / height);
            height = MAX_DIMENSION;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const FicheDecode = () => {
  // ====================== VERROU D'ACCÈS ======================
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessError, setAccessError] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [accessCode, setAccessCode] = useState(null); // code validé, réutilisé pour les analyses

  useEffect(() => {
    const stored = sessionStorage.getItem(ACCESS_STORAGE_KEY);
    if (stored) {
      setAccessCode(stored);
      setIsUnlocked(true);
    }
  }, []);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!accessCodeInput.trim()) return;

    setIsVerifying(true);
    setAccessError(null);

    try {
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessCode: accessCodeInput.trim(), validateOnly: true }),
      });

      if (response.ok) {
        sessionStorage.setItem(ACCESS_STORAGE_KEY, accessCodeInput.trim());
        setAccessCode(accessCodeInput.trim());
        setIsUnlocked(true);
      } else {
        const data = await response.json().catch(() => ({}));
        setAccessError(data.error || 'Code incorrect.');
      }
    } catch (err) {
      console.error(err);
      setAccessError('Impossible de vérifier le code. Réessaie dans quelques instants.');
    } finally {
      setIsVerifying(false);
    }
  };

  // ====================== ANALYSE DE LA FEUILLE ======================
  const [previewUrl, setPreviewUrl] = useState(null);
  const [resizedDataUrl, setResizedDataUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setResult(null);

    if (!file.type.startsWith('image/')) {
      setError('Merci de choisir un fichier image (photo ou scan de la feuille).');
      return;
    }

    try {
      const dataUrl = await resizeImage(file);
      setPreviewUrl(dataUrl);
      setResizedDataUrl(dataUrl);
    } catch (err) {
      console.error(err);
      setError("Impossible de lire cette image, essaie une autre photo.");
    }
  };

  const handleAnalyze = async () => {
    if (!resizedDataUrl) return;

    setIsLoading(true);
    setError(null);
    setResult(null);

    try {
      const [, mediaType, , base64Data] = resizedDataUrl.match(/^data:(.+?)(;base64)?,(.*)$/) || [];

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accessCode,
          imageBase64: base64Data,
          mediaType: mediaType || 'image/jpeg',
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          // Le code a pu être révoqué/changé entre-temps : on reverrouille.
          sessionStorage.removeItem(ACCESS_STORAGE_KEY);
          setIsUnlocked(false);
          setAccessError('Session expirée, merci de ressaisir le code.');
          return;
        }
        setError(data.error || "Une erreur s'est produite pendant l'analyse.");
        return;
      }

      setResult(data);
    } catch (err) {
      console.error(err);
      setError('Impossible de contacter le service. Réessaie dans quelques instants.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setPreviewUrl(null);
    setResizedDataUrl(null);
    setResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ====================== ÉCRAN DE VERROUILLAGE ======================
  if (!isUnlocked) {
    return (
      <div className="container mt-5 mb-5 text-center" style={{ maxWidth: '420px' }}>
        <h1 className="premium-title mb-3" style={{ fontSize: '1.8rem' }}>
          Décode ma feuille de note
        </h1>
        <p className="text-muted mb-4">
          Cette page est réservée aux membres. Entre le code d'accès pour continuer.
        </p>

        <form onSubmit={handleUnlock}>
          <input
            type="password"
            className="premium-search-input mb-3"
            style={{ width: '100%', textAlign: 'center' }}
            placeholder="Code d'accès"
            value={accessCodeInput}
            onChange={(e) => setAccessCodeInput(e.target.value)}
            autoFocus
          />

          {accessError && (
            <div className="premium-error mb-3">{accessError}</div>
          )}

          <button
            type="submit"
            className="btn"
            style={{ background: '#C8102E', color: 'white', width: '100%' }}
            disabled={isVerifying || !accessCodeInput.trim()}
          >
            {isVerifying ? 'Vérification...' : 'Déverrouiller'}
          </button>
        </form>
      </div>
    );
  }

  // ====================== PAGE PRINCIPALE ======================
  return (
    <div className="container mt-5 mb-5" style={{ maxWidth: '800px' }}>
      <h1 className="premium-title mb-3 text-center" style={{ fontSize: '2rem' }}>
        Décode ma feuille de note
      </h1>
      <p className="text-center text-muted mb-5">
        Prends en photo ta feuille de note ISU, on t'explique le score en langage clair.
      </p>

      {!previewUrl && (
        <div
          className="text-center p-5"
          style={{
            border: '2px dashed #C8102E',
            borderRadius: '16px',
            cursor: 'pointer',
            background: '#fafafa',
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <p className="mb-2" style={{ fontSize: '1.1rem', color: '#0a1f3d' }}>
            📸 Clique ici pour choisir une photo
          </p>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            JPEG, PNG ou WEBP
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {previewUrl && (
        <div className="text-center mb-4">
          <img
            src={previewUrl}
            alt="Feuille de note"
            style={{ maxWidth: '100%', maxHeight: '420px', borderRadius: '12px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)' }}
          />
          <div className="mt-3 d-flex justify-content-center gap-2">
            <button className="btn btn-outline-secondary" onClick={handleReset} disabled={isLoading}>
              Changer de photo
            </button>
            <button
              className="btn"
              style={{ background: '#C8102E', color: 'white' }}
              onClick={handleAnalyze}
              disabled={isLoading}
            >
              {isLoading ? 'Analyse en cours...' : 'Analyser la feuille'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="premium-error text-center mx-auto mb-4" style={{ maxWidth: '500px' }}>
          {error}
        </div>
      )}

      {result && (
        <div className="mt-4">
          <div className="premium-table p-4 mb-4">
            <h3 className="premium-section-title" style={{ fontSize: '1.4rem' }}>
              {result.patineur} <span className="text-muted" style={{ fontSize: '1rem' }}>({result.nation})</span>
            </h3>
            <div className="d-flex flex-wrap gap-4 mb-3">
              <div><strong>Rang :</strong> {result.rang}</div>
              <div><strong>Score total :</strong> {result.score_total}</div>
              <div><strong>Éléments :</strong> {result.score_elements}</div>
              <div><strong>Composantes :</strong> {result.score_composantes}</div>
            </div>
            <p style={{ lineHeight: 1.6 }}>{result.resume}</p>
          </div>

          <div className="row">
            <div className="col-md-6 mb-4">
              <div className="p-4 h-100" style={{ background: 'rgba(40, 167, 69, 0.08)', borderRadius: '12px', border: '1px solid rgba(40, 167, 69, 0.25)' }}>
                <h5 className="mb-3" style={{ color: '#198754' }}>✓ Points forts</h5>
                <ul className="mb-0 ps-3">
                  {(result.points_forts || []).map((p, i) => (
                    <li key={i} className="mb-2">{p}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="col-md-6 mb-4">
              <div className="p-4 h-100" style={{ background: 'rgba(200, 16, 46, 0.06)', borderRadius: '12px', border: '1px solid rgba(200, 16, 46, 0.2)' }}>
                <h5 className="mb-3" style={{ color: '#C8102E' }}>À travailler</h5>
                <ul className="mb-0 ps-3">
                  {(result.points_a_travailler || []).map((p, i) => (
                    <li key={i} className="mb-2">{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="text-center">
            <button className="btn btn-outline-secondary" onClick={handleReset}>
              Analyser une autre feuille
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FicheDecode;
